import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const StripeOnboarding = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    checkStripeStatus();
  }, []);

  const checkStripeStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/stripe/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(response.data);
    } catch (err) {
      setError('Failed to check Stripe account status');
      console.error('Stripe status error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setCreating(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/stripe/onboarding`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create onboarding link');
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payout Account Setup</h1>
              <p className="text-gray-600">Connect your bank account to receive earnings</p>
            </div>
          </div>
        </div>

        {/* Payout Safety Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <p className="text-blue-900 font-semibold mb-2">Your money is safe.</p>
              <p className="text-blue-800 text-sm mb-2">
                This step connects your bank account so RideHowl can pay you for completed rides.
                Stripe Connect is used for payouts to your account only. RideHowl cannot initiate any transfers or withdrawals from your bank account.
              </p>
              <p className="text-blue-800 text-sm">
                Subscription payments for higher driver tiers (if applicable) are handled separately through a different checkout screen and are not connected to your payout account.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Status Cards */}
        {status && (
          <div className="space-y-6">
            {/* Connection Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Account Status</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Connected to Stripe</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    status.connected
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {status.connected ? 'Yes' : 'No'}
                  </span>
                </div>

                {status.connected && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Onboarding Complete</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        !status.onboarding_required
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {!status.onboarding_required ? 'Yes' : 'Pending'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Payouts Enabled</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        status.payouts_enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {status.payouts_enabled ? 'Yes' : 'No'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Charges Enabled</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        status.charges_enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {status.charges_enabled ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Requirements */}
            {status.connected && status.requirements && (
              status.requirements.currently_due?.length > 0 ||
              status.requirements.past_due?.length > 0
            ) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">
                  Action Required
                </h3>
                {status.requirements.past_due?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-yellow-800 font-medium mb-2">Past Due Requirements:</p>
                    <ul className="list-disc list-inside text-yellow-700">
                      {status.requirements.past_due.map((req, idx) => (
                        <li key={idx}>{req.replace(/_/g, ' ')}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {status.requirements.currently_due?.length > 0 && (
                  <div>
                    <p className="text-yellow-800 font-medium mb-2">Currently Due:</p>
                    <ul className="list-disc list-inside text-yellow-700">
                      {status.requirements.currently_due.map((req, idx) => (
                        <li key={idx}>{req.replace(/_/g, ' ')}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Next Steps</h2>

              {!status.connected || status.onboarding_required ? (
                <div className="space-y-4">
                  <p className="text-gray-700">
                    To receive payouts from completed rides, you need to connect your bank account through Stripe.
                    This is a secure process that takes just a few minutes.
                  </p>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                    <p className="text-blue-800 text-sm">
                      <strong>What you'll need:</strong> Social Security Number (or EIN), Date of Birth,
                      Bank Account details (routing & account number), and a valid ID.
                    </p>
                  </div>
                  <button
                    onClick={handleStartOnboarding}
                    disabled={creating}
                    className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition ${
                      creating
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {creating ? 'Redirecting to Stripe...' : 'Connect Bank Account'}
                  </button>
                </div>
              ) : status.payouts_enabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-green-800 font-medium">
                      Your payout account is fully set up! You can now receive earnings from completed rides.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/driver/dashboard')}
                    className="w-full py-3 px-6 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                  >
                    Go to Dashboard
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Your account is being reviewed by Stripe. This usually takes a few minutes to a few hours.
                    You'll receive an email when your account is ready to receive payouts.
                  </p>
                  <button
                    onClick={checkStripeStatus}
                    className="w-full py-3 px-6 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                  >
                    Refresh Status
                  </button>
                </div>
              )}
            </div>

            {/* Help Section */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Need Help?</h3>
              <p className="text-gray-700 mb-3">
                If you're having trouble connecting your bank account or have questions about payouts:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Contact support at support@ridehowl.com</li>
                <li>Visit our Help Center for FAQs</li>
                <li>Payouts are typically processed within 2 business days</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StripeOnboarding;
